import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, Github, Linkedin, Twitter, Mail, Heart } from 'lucide-react';
import { profileData } from '../data/mock';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', href: '#home' },
    { label: 'Services', href: '#services' },
    { label: 'Portfolio', href: '#portfolio' },
    { label: 'About', href: '#about' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <footer className="py-16 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="flex items-center gap-3 mb-4"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00ff88] to-[#00cc6a] flex items-center justify-center">
                <span className="text-black font-bold text-sm">{profileData.initials}</span>
              </div>
              <span className="font-display font-semibold text-white text-lg">
                {profileData.firstName}<span className="text-[#00ff88]">.</span>
              </span>
            </motion.div>
            <p className="text-white/50 text-sm mb-6 max-w-md">
              {profileData.title}. Passionate about protecting digital infrastructure 
              and building secure systems that organizations can trust.
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              {[
                { icon: Github, href: profileData.social.github },
                { icon: Linkedin, href: profileData.social.linkedin },
                { icon: Twitter, href: profileData.social.twitter },
                { icon: Mail, href: `mailto:${profileData.email}` }
              ].map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/50 hover:text-[#00ff88] hover:border-[#00ff88]/30 transition-all"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon size={18} />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white/30 text-xs uppercase tracking-wider mb-4">Navigation</h4>
            <nav className="flex flex-col gap-2">
              {quickLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-white/50 text-sm hover:text-[#00ff88] transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white/30 text-xs uppercase tracking-wider mb-4">Contact</h4>
            <div className="space-y-2 text-sm">
              <p className="text-white/50">{profileData.email}</p>
              <p className="text-white/50">{profileData.phone}</p>
              <p className="text-white/50">{profileData.location}</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-sm">
            © {currentYear} {profileData.name}. All rights reserved.
          </p>
          
          <p className="text-white/30 text-sm flex items-center gap-1">
            Built with <Heart size={14} className="text-[#00ff88]" /> in Edmonton, Canada
          </p>

          <motion.button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-white/30 text-sm hover:text-[#00ff88] transition-colors"
            whileHover={{ y: -2 }}
          >
            Back to top
            <ArrowUp size={14} />
          </motion.button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
